import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Wifi, WifiOff, Heart, X, ChevronLeft, ChevronRight, Mic, MessageSquare, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoodIcon } from '../lib/moodIcons';
import formatEventDate from '../lib/formatEventDate';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 60;
// Close codes the server uses when the link itself is gone — don't reconnect.
const DEAD_LINK_CODES = [4404, 4410];

const isVisual = (m) => m.file_type === 'image' || m.file_type === 'video';

function MediaTile({ item, accent, onOpen }) {
  if (item.file_type === 'text') {
    return (
      <div className="aura-card rounded-xl p-5" style={{ boxShadow: `0 0 24px ${accent}25` }}>
        <MessageSquare className="w-4 h-4 text-champagne mb-3" />
        <p className="text-text-light whitespace-pre-wrap break-words leading-relaxed">{item.text_content}</p>
        <p className="text-text-muted text-sm mt-3">— {item.guest_name || 'Anonymous'}</p>
      </div>
    );
  }
  if (item.file_type === 'audio') {
    return (
      <div className="aura-card rounded-xl p-4" style={{ boxShadow: `0 0 24px ${accent}25` }}>
        <p className="text-text-light text-sm mb-3 flex items-center gap-2">
          <Mic className="w-4 h-4 text-champagne" /> Voice message from {item.guest_name || 'Anonymous'}
        </p>
        <audio src={`${API_URL}${item.url}`} controls preload="none" className="w-full" />
        {item.caption && <p className="text-text-muted text-sm mt-2">{item.caption}</p>}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="block w-full rounded-xl overflow-hidden relative group text-left"
      style={{ boxShadow: `0 0 30px ${accent}30` }}
      aria-label={`Open moment from ${item.guest_name || 'a guest'}`}
    >
      {item.file_type === 'video' ? (
        <video src={`${API_URL}${item.url}`} className="w-full" muted loop playsInline autoPlay preload="metadata" />
      ) : (
        <img src={`${API_URL}${item.url}`} alt={item.caption || ''} className="w-full" loading="lazy" />
      )}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-between gap-2">
          <span className="text-text-light text-sm truncate">{item.guest_name || 'Anonymous'}</span>
          <span className="text-text-muted text-xs inline-flex items-center gap-1 shrink-0">
            {item.mood && <MoodIcon mood={item.mood} className="w-4 h-4 text-champagne" />}
            {(item.hearts || 0) > 0 && (<><Heart className="w-3.5 h-3.5 ml-1" /> {item.hearts}</>)}
          </span>
        </div>
      </div>
    </button>
  );
}

export default function LiveViewPage() {
  const { token } = useParams();
  const [aura, setAura] = useState(null);
  const [media, setMedia] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deadLink, setDeadLink] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [newItem, setNewItem] = useState(null);
  const [lightboxId, setLightboxId] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const retryRef = useRef(0);
  const stoppedRef = useRef(false);
  const hasConnectedRef = useRef(false);
  const mediaRef = useRef([]);
  useEffect(() => { mediaRef.current = media; }, [media]);

  const mergeNewest = useCallback((incoming) => {
    setMedia((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const fresh = incoming.filter((m) => !seen.has(m.id));
      return fresh.length ? [...fresh, ...prev].sort((a, b) => b.id - a.id) : prev;
    });
  }, []);

  const fetchPage = useCallback(async (beforeId) => {
    const params = { limit: PAGE_SIZE };
    if (beforeId) params.before_id = beforeId;
    const { data } = await axios.get(`${API_URL}/api/live/${token}`, { params });
    return data;
  }, [token]);

  const loadInitial = useCallback(async () => {
    try {
      const data = await fetchPage();
      setAura(data.aura);
      setCount(data.aura.media_count || 0);
      setMedia(data.media || []);
      setHasMore(!!data.has_more);
    } catch (err) {
      if (err.response?.status === 404) setDeadLink(true);
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = async () => {
    if (!media.length) return;
    setLoadingMore(true);
    try {
      const data = await fetchPage(media[media.length - 1].id);
      setMedia((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...(data.media || []).filter((m) => !seen.has(m.id))];
      });
      setHasMore(!!data.has_more);
    } catch (err) {
      if (err.response?.status === 404) setDeadLink(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const connect = useCallback(() => {
    if (stoppedRef.current) return;
    const wsBase = (API_URL || window.location.origin).replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/api/ws/live/${token}`);
    wsRef.current = ws;
    let heartbeat;

    ws.onopen = () => {
      setIsConnected(true);
      retryRef.current = 0;
      heartbeat = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send('ping'); }, 25000);
      // After a drop, catch up on anything uploaded while we were offline.
      if (hasConnectedRef.current) {
        fetchPage().then((d) => { mergeNewest(d.media || []); setCount(d.aura.media_count || 0); }).catch(() => {});
      }
      hasConnectedRef.current = true;
    };

    ws.onmessage = (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch { return; }
      if (msg.event === 'media_added' && msg.media) {
        if (mediaRef.current.some((m) => m.id === msg.media.id)) return;
        mergeNewest([msg.media]);
        setCount((c) => c + 1);
        setNewItem(msg.media);
        setTimeout(() => setNewItem(null), 3500);
      } else if (msg.event === 'media_removed') {
        setCount((c) => Math.max(0, c - 1));
        setMedia((prev) => prev.filter((m) => m.id !== msg.media_id));
        setLightboxId((id) => (id === msg.media_id ? null : id));
      } else if (msg.event === 'media_updated' && msg.media) {
        setMedia((prev) => prev.map((m) => (m.id === msg.media.id ? { ...m, ...msg.media } : m)));
      } else if (msg.event === 'reaction') {
        setMedia((prev) => prev.map((m) => (m.id === msg.media_id ? { ...m, hearts: msg.hearts, glows: msg.glows } : m)));
      }
    };

    ws.onclose = (event) => {
      clearInterval(heartbeat);
      setIsConnected(false);
      if (stoppedRef.current) return;
      if (DEAD_LINK_CODES.includes(event.code)) {
        stoppedRef.current = true;
        setDeadLink(true);
        return;
      }
      const delay = Math.min(30000, 2000 * 2 ** retryRef.current);
      retryRef.current += 1;
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => ws.close();
  }, [token, fetchPage, mergeNewest]);

  useEffect(() => {
    stoppedRef.current = false;
    loadInitial();
    connect();
    return () => {
      stoppedRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [loadInitial, connect]);

  // Lightbox navigation (photos & videos only)
  const visual = media.filter(isVisual);
  const lbIndex = visual.findIndex((m) => m.id === lightboxId);
  const lbItem = lbIndex >= 0 ? visual[lbIndex] : null;
  const step = useCallback((dir) => {
    if (lbIndex < 0 || !visual.length) return;
    setLightboxId(visual[(lbIndex + dir + visual.length) % visual.length].id);
  }, [lbIndex, visual]);

  useEffect(() => {
    if (!lbItem) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxId(null);
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lbItem, step]);

  useEffect(() => {
    if (aura?.title) document.title = `${aura.title} · Live · Memouraa`;
  }, [aura]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <p className="text-champagne font-serif text-xl">Connecting to the live view…</p>
        </div>
      </div>
    );
  }

  if (deadLink && !aura) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="live-view-inactive">
          <Sparkles className="w-10 h-10 text-champagne/60 mx-auto mb-4" />
          <h1 className="text-2xl font-serif text-text-light mb-2">This live view isn’t available</h1>
          <p className="text-text-muted mb-6">The host may have turned the link off or replaced it with a new one. Ask them for the latest link.</p>
          <Link to="/" className="text-champagne hover:underline">Visit Memouraa</Link>
        </div>
      </div>
    );
  }

  const accent = aura?.accent_color || '#D4AF37';

  return (
    <div className="min-h-screen bg-black relative" data-testid="live-view-page">
      <div className="fixed inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background: `radial-gradient(ellipse at 30% 20%, ${aura?.primary_color || '#8E44AD'}40 0%, transparent 50%),
                         radial-gradient(ellipse at 70% 80%, ${accent}40 0%, transparent 50%)`,
          }}
        />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/70 backdrop-blur border-b border-champagne/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif text-text-light hidden sm:inline">Memouraa</span>
          </Link>
          <div className="flex items-center gap-2 text-sm" aria-live="polite">
            {deadLink ? (
              <span className="text-text-muted">Live view ended</span>
            ) : isConnected ? (
              <><span className="w-2 h-2 rounded-full bg-success animate-pulse" /><Wifi className="w-4 h-4 text-success" /><span className="text-success">Live</span></>
            ) : (
              <><WifiOff className="w-4 h-4 text-error" /><span className="text-error">Reconnecting…</span></>
            )}
          </div>
        </div>
      </header>

      <main className="relative max-w-7xl mx-auto px-4 sm:px-8 pb-20">
        <div className="text-center pt-8 pb-8">
          <h1 className="text-3xl sm:text-5xl font-serif font-medium text-text-light mb-3 break-words">{aura?.title}</h1>
          <p className="text-text-muted flex items-center justify-center gap-2 flex-wrap">
            {aura?.event_date && (<span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />{formatEventDate(aura)}</span>)}
            {aura?.host_name && <span>· hosted by {aura.host_name}</span>}
          </p>
          <p className="text-champagne mt-2">{count} {count === 1 ? 'moment' : 'moments'} shared</p>
          {deadLink && (
            <p className="text-text-muted text-sm mt-3">The host turned off this live view, so new moments won’t appear.</p>
          )}
        </div>

        {media.length === 0 ? (
          <div className="flex items-center justify-center h-[45vh]">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-aura-purple/20 to-aura-gold/20 flex items-center justify-center mx-auto mb-5">
                <Sparkles className="w-10 h-10 text-champagne/50" />
              </div>
              <p className="text-2xl font-serif text-text-muted">Waiting for the first moments…</p>
              <p className="text-text-muted mt-2">Keep this page open — photos appear here as guests share them.</p>
            </div>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4">
            <AnimatePresence initial={false}>
              {media.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  className="break-inside-avoid mb-4"
                >
                  <MediaTile item={item} accent={accent} onOpen={() => setLightboxId(item.id)} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {hasMore && (
          <div className="text-center mt-8">
            <button onClick={loadMore} disabled={loadingMore} className="btn-outline px-6 py-2.5" data-testid="live-load-more">
              {loadingMore ? 'Loading…' : 'Load earlier moments'}
            </button>
          </div>
        )}
      </main>

      {/* New-moment toast */}
      <AnimatePresence>
        {newItem && (
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 w-max max-w-[calc(100vw-2rem)]"
          >
            <div className="aura-card rounded-full px-5 py-2.5 flex items-center gap-3 aura-glow">
              <span className="w-2 h-2 bg-champagne rounded-full animate-pulse shrink-0" />
              <span className="text-text-light text-sm truncate">New moment from {newItem.guest_name || 'a guest'}</span>
              {newItem.mood && <MoodIcon mood={newItem.mood} className="w-4 h-4 text-champagne shrink-0" />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lbItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
            onClick={() => setLightboxId(null)}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between p-4 text-text-light" onClick={(e) => e.stopPropagation()}>
              <span className="text-sm truncate">{lbItem.guest_name || 'Anonymous'}{lbItem.caption ? ` — ${lbItem.caption}` : ''}</span>
              <button onClick={() => setLightboxId(null)} className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Close">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-2 sm:px-16 pb-6 relative min-h-0">
              {lbItem.file_type === 'video' ? (
                <video key={lbItem.id} src={`${API_URL}${lbItem.url}`} controls autoPlay playsInline className="max-h-full max-w-full" onClick={(e) => e.stopPropagation()} />
              ) : (
                <img key={lbItem.id} src={`${API_URL}${lbItem.url}`} alt={lbItem.caption || ''} className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
              )}
              {visual.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); step(-1); }} className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-text-light" aria-label="Previous">
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); step(1); }} className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-text-light" aria-label="Next">
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="relative text-center pb-8">
        <p className="text-text-muted text-sm">
          Shared with <Link to="/" className="text-champagne hover:underline">Memouraa</Link>
        </p>
      </footer>
    </div>
  );
}
